import type { AxiosError } from 'axios';
import type { ErrorInterface } from './types';

export function createResponseErrorInterceptor(
    generalErrors: Record<string, string>,
): (axiosError: AxiosError<any>) => Promise<never> {
    const getErrorMessages = (code: number, message?: string): string => {
        if (message) {
            return message;
        }

        if (!window.navigator.onLine) {
            return generalErrors.offline || generalErrors.default;
        }

        if (generalErrors[code]) {
            return generalErrors[code];
        }

        return generalErrors.default;
    };

    return (axiosError: AxiosError<any>) => {
        const apiError = axiosError.response?.data;
        const status = axiosError.response?.status ?? 0;

        const isNetworkError =
            !axiosError.response ||
            axiosError.code === 'ECONNABORTED' ||
            !axiosError.response.status;

        const isRetirable =
            status === 0 || // network error
            status === 429 || // too many requests
            status >= 500 || // server error
            status === 408 || // request timeout
            status === 423; // locked

        const isServerError = status >= 500;
        const isAuthError = status === 401;
        const isPermissionError = status === 403;

        const shared = {
            code: status,
            isRetirable,
            isServerError,
            isAuthError,
            isPermissionError,
            isNetworkError,
            isOfflineError: !window.navigator.onLine,
            config: axiosError.config,
        } as const;

        if (!apiError) {
            return Promise.reject({
                message: getErrorMessages(status),
                type: 'request',
                metadata: null,
                ...shared,
            } as ErrorInterface);
        }

        return Promise.reject({
            message: getErrorMessages(status, apiError.message),
            type: apiError.errorType ?? 'api',
            metadata: apiError.metadata ?? null,
            ...shared,
        } as ErrorInterface);
    };
}
